import React, { useState, useEffect } from 'react';
import { employeeApi } from '../../lib/api';
import { departmentsApi, type Department } from '../../lib/api/departments.api';
import Button from '../../components/common/Button';

interface Employee {
    id: string;
    dni: string;
    name: string;
    surname?: string;
    email?: string;
    phone?: string;
    position?: string;
    departmentId?: string;
    department?: {
        id: string;
        name: string;
    } | string | undefined;
    contractType?: string;
    active: boolean;
}

const UtilidadesEmpleadosPage: React.FC = () => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);

    const [fieldToChange, setFieldToChange] = useState<string>('');
    const [newValue, setNewValue] = useState<string>('');

    const [filterName, setFilterName] = useState<string>('');
    const [filterDepartment, setFilterDepartment] = useState<string>('');

    useEffect(() => {
        loadEmployees();
        loadDepartments();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [filterName, filterDepartment, employees]);

    const loadEmployees = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await employeeApi.getEmployees();
            if (response.success && response.data) {
                setEmployees(response.data.employees || []);
            }
        } catch (error: any) {
            setError(error.message || 'Error al cargar empleados');
        } finally {
            setLoading(false);
        }
    };

    const loadDepartments = async () => {
        try {
            const depts = await departmentsApi.getDepartments();
            setDepartments(depts);
        } catch (error: any) {
            console.error('Error al cargar departamentos:', error);
        }
    };

     const applyFilters = () => {
        let filtered = employees;

        if (filterName.trim()) {
            const searchLower = filterName.toLowerCase();
            filtered = filtered.filter(emp =>
                `${emp.name} ${emp.surname || ''}`.toLowerCase().includes(searchLower) ||
                emp.dni.toLowerCase().includes(searchLower)
            );
        }

        if (filterDepartment) {
            filtered = filtered.filter(emp => {
                const deptId = emp.department && typeof emp.department === 'object' ? emp.department.id : emp.departmentId;
                return deptId === filterDepartment;
            });
        }

        setFilteredEmployees(filtered);
    };

    const clearFilters = () => {
        setFilterName('');
        setFilterDepartment('');
    };

    const toggleEmployeeSelection = (employeeId: string) => {
        const newSelected = new Set(selectedEmployees);
        if (newSelected.has(employeeId)) {
            newSelected.delete(employeeId);
        } else {
            newSelected.add(employeeId);
        }
        setSelectedEmployees(newSelected);
    };

    const toggleAllEmployees = () => {
        if (selectedEmployees.size === filteredEmployees.length) {
            setSelectedEmployees(new Set());
        } else {
            setSelectedEmployees(new Set(filteredEmployees.map(e => e.id)));
        }
    };

    const handleUpdate = async () => {
        if (selectedEmployees.size === 0) {
            setError('Selecciona al menos un empleado');
            return;
        }

        if (!fieldToChange) {
            setError('Selecciona el campo a cambiar');
            return;
        }

        if (!newValue) {
            setError('Ingresa el nuevo valor');
            return;
        }

        try {
            setUpdating(true);
            setError(null);
            setSuccess(null);

            const response = await employeeApi.batchUpdateEmployees(
                Array.from(selectedEmployees),
                fieldToChange,
                newValue
            );

            if (response.success) {
                setSuccess(`Se han actualizado ${selectedEmployees.size} empleados correctamente`);
                setSelectedEmployees(new Set());
                setFieldToChange('');
                setNewValue('');
                await loadEmployees();
            } else {
                setError(response.message || 'Error al actualizar empleados');
            }
        } catch (error: any) {
            setError(error.message || 'Error al actualizar empleados');
        } finally {
            setUpdating(false);
        }
     }

     if (loading) {
        return (
            <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Utilidades de Empleados</h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Edición masiva de empleados
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                    {success}
                </div>
            )}

            {/* Formulario de edición masiva */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Editar {selectedEmployees.size > 0 ? `${selectedEmployees.size} empleados seleccionados` : 'empleados'}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Campo a Cambiar
                        </label>
                        <select
                            value={fieldToChange}
                            onChange={(e) => setFieldToChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Seleccionar campo...</option>
                            <option value="departmentId">Departamento</option>
                            <option value="position">Posición</option>
                            <option value="contractType">Tipo de Contrato</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nuevo Valor
                        </label>
                        <input
                            type="text"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            placeholder={fieldToChange === 'departmentId' ? 'ID del departamento' : 'Nuevo valor'}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>

                <div className="mt-4">
                    <Button
                        onClick={handleUpdate}
                        disabled={selectedEmployees.size === 0 || !fieldToChange || !newValue || updating}
                        loading={updating}
                    >
                        {updating ? 'Actualizando...' : 'Aplicar Cambios'}
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Buscar por nombre/DNI
                        </label>
                        <input
                            type="text"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            placeholder="Nombre, apellidos o DNI..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Filtrar por departamento
                        </label>
                        <select
                            value={filterDepartment}
                            onChange={(e) => setFilterDepartment(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Todos los departamentos</option>
                            {departments.map(dept => (
                                <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={clearFilters}
                            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Limpiar filtros
                        </button>
                    </div>
                </div>
            </div>

            {/* Lista de empleados */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={selectedEmployees.size === filteredEmployees.length && filteredEmployees.length > 0}
                                onChange={toggleAllEmployees}
                                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Seleccionar todos ({filteredEmployees.length} empleados)
                            </span>
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedEmployees.size} seleccionados
                        </span>
                    </div>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredEmployees.map((employee) => (
                        <div
                            key={employee.id}
                            className="flex items-center px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                            <input
                                type="checkbox"
                                checked={selectedEmployees.has(employee.id)}
                                onChange={() => toggleEmployeeSelection(employee.id)}
                                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                            />
                            <div className="ml-4 flex-1">
                                <div className="flex items-center">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {employee.name} {employee.surname}
                                    </p>
                                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                                        employee.active
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                        {employee.active ? 'Activo' : 'Inactivo'}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    DNI: {employee.dni}
                                </p>
                                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 space-x-2">
                                    {employee.position && <span>{employee.position}</span>}
                                    {employee.department && <span>• {typeof employee.department === 'object' ? employee.department.name : employee.department}</span>}
                                    {employee.contractType && <span>• {employee.contractType}</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UtilidadesEmpleadosPage;
